import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import PropTypes from "prop-types";
import { LOADING_BAR_STATUS } from "../constants/loadingBarStatus";

const FINISH_TO_HIDDEN_DELAY = 800;
const DEFAULT_PULSE_DURATION = 300;

export const LoadingBarContext = createContext({
	status: LOADING_BAR_STATUS.HIDDEN,
	show: () => {},
	finish: () => {},
	pulse: () => {},
});

export const LoadingBarProvider = ({ children }) => {
	const [status, setStatus] = useState(LOADING_BAR_STATUS.HIDDEN);
	const hideTimer = useRef(null);
	const pulseTimer = useRef(null);

	const clearHideTimer = useCallback(() => {
		if (hideTimer.current) {
			clearTimeout(hideTimer.current);
			hideTimer.current = null;
		}
	}, []);

	const clearPulseTimer = useCallback(() => {
		if (pulseTimer.current) {
			clearTimeout(pulseTimer.current);
			pulseTimer.current = null;
		}
	}, []);

	const show = useCallback(() => {
		clearHideTimer();
		setStatus(LOADING_BAR_STATUS.LOADING);
	}, [clearHideTimer]);

	const finish = useCallback(() => {
		clearHideTimer();
		setStatus(LOADING_BAR_STATUS.FINISHED);
		hideTimer.current = setTimeout(() => {
			setStatus(LOADING_BAR_STATUS.HIDDEN);
			hideTimer.current = null;
		}, FINISH_TO_HIDDEN_DELAY);
	}, [clearHideTimer]);

	const pulse = useCallback(
		(duration = DEFAULT_PULSE_DURATION) => {
			clearPulseTimer();
			show();
			pulseTimer.current = setTimeout(() => {
				finish();
				pulseTimer.current = null;
			}, duration);
		},
		[show, finish, clearPulseTimer]
	);

	useEffect(() => {
		return () => {
			clearHideTimer();
			clearPulseTimer();
		};
	}, [clearHideTimer, clearPulseTimer]);

	const value = useMemo(() => ({ status, show, finish, pulse }), [status, show, finish, pulse]);

	return <LoadingBarContext.Provider value={value}>{children}</LoadingBarContext.Provider>;
};

LoadingBarProvider.propTypes = {
	children: PropTypes.node,
};

export const useLoadingBar = () => useContext(LoadingBarContext);
